import { ApexOptions } from 'apexcharts';
import constants from 'constants/constants';
import moment from 'moment-timezone';
import colorsVars from 'styles/colors.module.scss';
import { addTestIdsToElements } from 'utilitys/helpers/general';

import { QueryResponse } from 'api/queries/getResourceChart.query';

import { Range } from 'components/common/GlobalFiltersInPage/components/RangeFilter/rangeFilter';

const {
    resourcesPastPlannedDark,
    resourcesPastActualDark,
    resourcesFuturePlannedDark,
    resourcesPastPlannedLight,
    resourcesPastActualLight,
    resourcesFuturePlannedLight,
} = colorsVars;

export const categoriesKeys = [
    {
        key: 'budgetUnit',
        series: 'budgetBarId',
        label: 'Planned Budget',
        color: {
            light: resourcesPastPlannedLight,
            dark: resourcesPastPlannedDark,
        },
    },
    {
        key: 'actualUnit',
        series: 'actualBarId',
        label: 'Actual',
        color: {
            light: resourcesPastActualLight,
            dark: resourcesPastActualDark,
        },
    },
    {
        key: 'remainingUnit',
        series: 'remainingBarId',
        label: 'Remaining',
        color: {
            light: resourcesFuturePlannedLight,
            dark: resourcesFuturePlannedDark,
        },
    },
];

const rangeDailyOptions: Range[] = ['R_2W', 'R_4W', 'R_3M', 'R_6M'];

export const serialize = ({
    data,
    range,
}: {
    data: QueryResponse['chart'] | undefined;
    range: Range | [number, number] | undefined;
}): { categories: string[]; series: ApexAxisChartSeries | ApexNonAxisChartSeries } => {
    if (!data || !range)
        return {
            categories: [],
            series: [],
        };
    const isCustomRange = Array.isArray(range) && range.length === 2;
    const isDailyRange = !Array.isArray(range) && rangeDailyOptions.includes(range);
    const format = isCustomRange || isDailyRange ? 'DD MMM YYYY' : 'MMM YYYY';
    const categories = data.map((item) => {
        return moment(item.startDate).format(format);
    });

    const series = categoriesKeys.map((item) => {
        return {
            name: item.label,
            data: data.map((d) => d[item.key]),
        };
    });

    return {
        categories,
        series,
    };
};

interface Props {
    series: ApexAxisChartSeries | ApexNonAxisChartSeries;
    categories: string[];
    themeStatus: boolean;
    dataPointSelection: (data) => void;
    versionDate: number | undefined;
    tooltipFormatter: (data) => string;
    calculatedVersionDate: number | undefined;
    actualKpi: number | undefined;
}

export const getChartConfig = ({
    series,
    categories,
    themeStatus,
    versionDate,
    dataPointSelection,
    tooltipFormatter,
    calculatedVersionDate,
    actualKpi,
}: Props): { options: ApexOptions; series: ApexOptions['series'] } => ({
    options: {
        colors: categoriesKeys.map((item) => (themeStatus ? item.color.light : item.color.dark)),
        dataLabels: {
            enabled: false,
        },
        plotOptions: {
            bar: {
                columnWidth: '80%',
            },
        },
        states: {
            active: {
                filter: {
                    type: 'darken',
                },
            },
            hover: {
                filter: {
                    type: 'darken',
                },
            },
        },
        tooltip: {
            x: {
                formatter: function (_value, { dataPointIndex }) {
                    return tooltipFormatter({ dataPointIndex });
                },
            },
            style: {
                fontFamily: 'Roboto',
                fontSize: '14px',
            },
        },
        grid: {
            borderColor: 'var(--border-secondary)',
        },
        chart: {
            background: themeStatus ? 'transparent' : '#ffffff',
            foreColor: themeStatus ? '#FEFEFE' : '#000000',
            toolbar: {
                tools: {
                    download: false,
                    selection: false,
                    zoom: true,
                    zoomin: true,
                    zoomout: true,
                    pan: false,
                    reset: true,
                },
            },
            events: {
                beforeZoom(chart: any) {
                    if (chart.wheelDelay) {
                        return {
                            xaxis: {
                                min: chart.minX,
                                max: chart.maxX,
                            },
                        };
                    }
                    return undefined;
                },
                dataPointSelection(_event, _chartContext, options) {
                    dataPointSelection(options);
                },
                updated: () => {
                    addTestIdsToElements('.apexcharts-bar-series .apexcharts-series path', 'resources-analysis-chart');
                },
            },
        },
        xaxis: {
            type: 'datetime',
            categories,
            labels: {
                datetimeUTC: false,
                datetimeFormatter: {
                    year: 'yyyy',
                    month: 'MMM yyyy',
                    day: 'dd MMM',
                },
                style: {
                    fontFamily: 'Roboto',
                    fontSize: '14px',
                },
            },
        },
        annotations: {
            xaxis: [
                {
                    x: calculatedVersionDate,
                    borderWidth: 2,
                    strokeDashArray: 0,
                    borderColor: '#775DD0',
                    label: {
                        offsetY: 50,
                        borderColor: '#775DD0',
                        orientation: 'horizontal',
                        style: {
                            color: '#fff',
                            background: '#775DD0',
                        },
                        text: moment(versionDate).format(constants.formats.date.default),
                    },
                },
            ],
            yaxis: [
                {
                    y: actualKpi,
                    strokeDashArray: 5,
                    borderColor: themeStatus ? resourcesPastActualLight : resourcesPastActualDark,
                    borderWidth: 3,
                    label: {
                        position: 'right',
                        borderColor: 'transparent',
                        offsetY: -5,
                        text: `Actual (${actualKpi} units)`,
                        style: {
                            color: themeStatus ? resourcesPastActualLight : resourcesPastActualDark,
                            fontFamily: 'Roboto',
                            fontSize: '16px',
                        },
                    },
                },
            ],
        },
        legend: {
            fontSize: '14px',
            fontFamily: 'Roboto',
        },
    },

    series,
});
